import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ImageOff, Palette, Trash2, Upload, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/errors";

const ACCEPTED_MIME = "image/svg+xml,image/png,image/jpeg,image/webp";
const MAX_LOGO_BYTES = 1_000_000;

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/**
 * Self-serve logo uploader. Renders the current header logo at the same
 * 32px height the sidebar uses, plus a drop/file picker, a live preview
 * (FileReader) and a Remove button guarded by a confirm dialog.
 */
function LogoUploader({ slug, currentUrl, canWrite, onChanged }) {
  const inputRef = useRef(null);
  const [selected, setSelected] = useState(null); // { file, dataUrl }
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const pick = (file) => {
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo is ${humanSize(file.size)} — limit is 1 MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSelected({ file, dataUrl: reader.result });
    reader.onerror = () => toast.error("Couldn't read the file.");
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!canWrite) return;
    pick(e.dataTransfer.files?.[0]);
  };

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", selected.file);
      const { data } = await api.post(`/admin/tenants/${slug}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Logo updated · ${humanSize(data.size_bytes)}`);
      setSelected(null);
      if (inputRef.current) inputRef.current.value = "";
      await onChanged(data.logo_url);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${slug}/logo`);
      toast.success("Logo removed — header now shows initials.");
      setConfirmRemove(false);
      await onChanged(null);
    } catch (err) {
      toast.error(formatApiError(err, "Remove failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="logo-uploader">
      <div>
        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Current logo</Label>
        <div className="mt-1.5 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt="Current tenant logo"
              data-testid="logo-current-preview"
              className="h-8 w-auto max-w-[12rem] object-contain"
            />
          ) : (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ImageOff className="h-4 w-4" />
              <span data-testid="logo-current-empty">No logo set — header falls back to initials.</span>
            </div>
          )}
        </div>
      </div>

      {canWrite && (
        <>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Upload new logo</Label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="mt-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-5 text-center"
            >
              <Upload className="mx-auto h-5 w-5 text-slate-400" />
              <p className="mt-2 text-xs text-slate-500">
                Drop a file here, or
                {" "}
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="font-medium text-cargodash-primary-600 hover:underline"
                  data-testid="logo-pick-btn"
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-[11px] text-slate-400">SVG, PNG, JPG, WEBP · max 1 MB</p>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_MIME}
                className="hidden"
                data-testid="logo-file-input"
                onChange={(e) => pick(e.target.files?.[0])}
              />
            </div>
          </div>

          {selected && (
            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="logo-preview-card">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">Preview</div>
                  <div className="mt-1.5 flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                    <img
                      src={selected.dataUrl}
                      alt="Preview"
                      data-testid="logo-new-preview"
                      className="h-8 w-auto max-w-[12rem] object-contain"
                    />
                    <span className="ml-auto text-[10px] uppercase tracking-[0.16em] text-slate-500">Hub</span>
                  </div>
                  <div className="mt-2 truncate text-[11px] text-slate-500">
                    {selected.file.name} · {humanSize(selected.file.size)}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  data-testid="logo-save-btn"
                  className="bg-cargodash-primary-600 text-white hover:bg-cargodash-primary-700"
                >
                  {busy ? "Saving…" : "Save logo"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSelected(null);
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                  disabled={busy}
                  data-testid="logo-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {currentUrl && !selected && (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              data-testid="logo-remove-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-cargodash-alert-600 hover:underline"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove current logo
            </button>
          )}
        </>
      )}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent data-testid="logo-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove tenant logo?</AlertDialogTitle>
            <AlertDialogDescription>
              The header will fall back to initials + tenant name until a new logo is uploaded. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={busy}
              data-testid="logo-remove-confirm-btn"
              className="bg-cargodash-alert-600 hover:bg-cargodash-alert-600/90"
            >
              {busy ? "Removing…" : "Remove logo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BrandingTab() {
  const { refresh, activeTenant, hasPerm } = useAuth();
  const canWrite = hasPerm("tenant.manage");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/tenant/branding");
    setData(data);
    setDraft({
      primary_color: data.branding?.primary_color || "#0f172a",
      accent_color: data.branding?.accent_color || "#d4af37",
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveColours = async (e) => {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if (draft.primary_color !== (data.branding?.primary_color || "")) fd.append("primary_color", draft.primary_color);
      if (draft.accent_color !== (data.branding?.accent_color || "")) fd.append("accent_color", draft.accent_color);
      if (![...fd.keys()].length) {
        toast.info("No colour changes to save.");
        setSaving(false);
        return;
      }
      await api.patch("/admin/tenant/branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Colours updated");
      await load();
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChanged = async () => {
    await load();
    await refresh();
  };

  if (!data || !draft) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const slug = activeTenant?.slug || data.slug;
  const currentLogo = data.branding?.logo_url || null;

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-settings-branding">
      <div className="space-y-6 rounded-xl border border-slate-200 bg-white p-5">
        <div>
          <Label>Tenant name</Label>
          <Input value={data.name || ""} readOnly className="bg-slate-50 font-mono text-xs" />
          <p className="mt-1 text-[11px] text-slate-500">Contact your platform admin to rename the tenant.</p>
        </div>

        <LogoUploader
          slug={slug}
          currentUrl={currentLogo}
          canWrite={canWrite}
          onChanged={handleLogoChanged}
        />

        {canWrite && (
          <form onSubmit={saveColours} className="space-y-3 border-t border-slate-200 pt-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary colour</Label>
                <Input
                  type="color"
                  value={draft.primary_color}
                  data-testid="settings-primary"
                  onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })}
                />
              </div>
              <div>
                <Label>Accent colour</Label>
                <Input
                  type="color"
                  value={draft.accent_color}
                  data-testid="settings-accent"
                  onChange={(e) => setDraft({ ...draft, accent_color: e.target.value })}
                />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="settings-save" className="bg-slate-900 hover:bg-slate-800">
              {saving ? "Saving…" : "Save colours"}
            </Button>
          </form>
        )}
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Live preview</div>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <div
            className="flex items-center gap-3 px-4 py-3"
            style={{ background: draft.primary_color, color: "#ffffff" }}
          >
            {currentLogo ? (
              <img src={currentLogo} className="h-7 max-w-[10rem] object-contain" alt="" />
            ) : (
              <span className="font-display text-sm font-semibold">{data.name}</span>
            )}
            <span
              className="ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ background: draft.accent_color, color: draft.primary_color }}
            >
              Accent
            </span>
          </div>
          <div className="space-y-2 bg-white px-4 py-4 text-xs text-slate-600">
            <div>
              <span className="font-semibold" style={{ color: draft.primary_color }}>
                Headline in your primary
              </span>
            </div>
            <div className="h-8 w-32 rounded-md" style={{ background: draft.primary_color }} />
            <div className="h-1.5 w-24 rounded-full" style={{ background: draft.accent_color }} />
          </div>
        </div>
        <div className="text-[11px] text-slate-500">
          Palette applies across the Hub and on SSO launch to partner apps.
        </div>
      </div>
    </div>
  );
}

function MembersTab() {
  const { hasPerm } = useAuth();
  return (
    <div data-testid="admin-settings-members" className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
          <Users className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-base font-semibold text-slate-900">Tenant members</h3>
          <p className="mt-1 text-sm text-slate-500">
            Members are managed in the full Users page — create accounts, edit roles, reset passwords and disable access.
          </p>
          {hasPerm("users.read") ? (
            <Link
              to="/admin/users"
              data-testid="settings-open-users"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
            >
              Open Users <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <div className="mt-3 text-xs text-slate-500">You don't have permission to manage users.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { activeTenant } = useAuth();
  return (
    <div data-testid="admin-settings" className="mx-auto w-full max-w-5xl space-y-6">
      <Breadcrumbs />
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          Tenant settings
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {activeTenant?.name
            ? `Local settings for ${activeTenant.name}.`
            : "Local settings for the active tenant."}
        </p>
      </div>
      <Tabs defaultValue="branding" className="w-full">
        <TabsList data-testid="admin-settings-tabs">
          <TabsTrigger value="branding" data-testid="settings-tab-branding" className="gap-2">
            <Palette className="h-3.5 w-3.5" />Branding
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="settings-tab-members" className="gap-2">
            <Users className="h-3.5 w-3.5" />Members
          </TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="mt-4">
          <BrandingTab />
        </TabsContent>
        <TabsContent value="members" className="mt-4">
          <MembersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
